import React, { useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { LevelBadge } from '../../ui/LevelBadge';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { WhyPopover } from '../../ui/WhyPopover';
import { Feedback } from './Feedback';
import { getAgent } from '../../../utils/agents';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import type { ActionLevel, AgentId, Processing } from '../../../types/anna';
interface WallCardProps {
  agent: AgentId;
  kicker?: string;
  title: string;
  level?: ActionLevel;
  where: Processing;
  traceId: string;
  feedback?: boolean;
  /** Extra actions revealed with the details (e.g. Aanpassen / Afwijzen) */
  actions?: React.ReactNode;
  size?: 'orbit' | 'focus';
  children?: React.ReactNode;
}
const LONG_PRESS_MS = 450;
const EASE = [0.23, 1, 0.32, 1] as const;
const isControl = (target: EventTarget | null) => target instanceof Element && !!target.closest('button, a, input, textarea, [role="slider"], [role="dialog"]');

/**
 * Summoned card on the wall (all text ≥ 22 px). Content first; the chrome stays out of the way:
 * one subtle line at the bottom. Tap or long-press reveals level, lokaal/cloud and "Waarom?".
 */
export function WallCard({
  agent,
  kicker,
  title,
  level,
  where,
  traceId,
  feedback = false,
  actions,
  size = 'orbit',
  children
}: WallCardProps) {
  const a = getAgent(agent);
  const reduce = useMotionPreference();
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const detailsId = useId();
  const focus = size === 'focus';
  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  return <article className={`glass w-full rounded-[28px] text-left ${focus ? 'p-7 xl:p-8' : 'p-6'}`} onPointerDown={e => {
    if (isControl(e.target)) return;
    longPressed.current = false;
    clear();
    timer.current = window.setTimeout(() => {
      longPressed.current = true;
      setOpen(true);
    }, LONG_PRESS_MS);
  }} onPointerUp={clear} onPointerLeave={clear} onPointerCancel={clear} onClick={e => {
    if (isControl(e.target)) return;
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    setOpen(v => !v);
  }} data-id="element-1408" data-mp="p0je3ktdwxyw">
      <h3 className={`font-semibold leading-tight text-ink ${focus ? 'text-[34px]' : 'text-[28px]'}`} data-id="element-1409" data-mp="264g4esrs8880">{title}</h3>
      {children && <div className={`mt-4 leading-snug text-ink-soft ${focus ? 'text-[24px]' : 'text-[22px]'}`} data-id="element-1410" data-mp="2a61lyrf9haoj">{children}</div>}

      <button type="button" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(v => !v)} className="mt-5 flex w-full items-center gap-3 rounded-sm border-t border-white/[0.07] pt-4 text-left text-[22px] text-ink-muted transition-colors duration-150 hover:text-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-1411" data-mp="2f77lb83ffw71">
        <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
        backgroundColor: a.color
      }} data-id="element-1412" data-mp="3vs9f6xb27lye" />
        <span className="min-w-0 flex-1 truncate" data-id="element-1413" data-mp="3mxl954xnu08j">
          {a.name}
          {kicker ? ` · ${kicker}` : ''}
        </span>
        <span className="sr-only" data-id="element-1414" data-mp="3hys6y8ntpvno">{open ? 'Verberg details' : 'Toon niveau, verwerking en waarom'}</span>
        <ChevronDownIcon aria-hidden className={`h-5 w-5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-1415" data-mp="3cz5rdskiw3bp" />
      </button>

      <AnimatePresence initial={false} data-id="element-1416" data-mp="382f5gom1edid">
        {open && <motion.div id={detailsId} key="details" initial={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        height: 'auto',
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="overflow-hidden" data-id="element-1417" data-mp="33zsgcvgr36ov">
            <div className="flex flex-wrap items-center gap-2 pt-4" data-id="element-1418" data-mp="1e8cg608cb6v1">
              {level && <LevelBadge level={level} size="wall" data-id="element-1419" data-mp="10anvase9vzpz" />}
              <ProcessingTag where={where} size="wall" data-id="element-1420" data-mp="10c8lk5tk0wi1" />
              <span className="ml-auto flex items-center gap-1" data-id="element-1421" data-mp="14a6bxbz29on0">
                {feedback && <Feedback data-id="element-1422" data-mp="qjt4daq30by7" />}
                <WhyPopover traceId={traceId} size="wall" data-id="element-1423" data-mp="qii2jgg75e9q" />
              </span>
            </div>
            {actions && <div className="mt-3 flex flex-wrap gap-2" data-id="element-1424" data-mp="vc43wcgwtvq8">{actions}</div>}
          </motion.div>}
      </AnimatePresence>
    </article>;
}